import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Button primitive — shared IQ-family house style (mirrors AdsIQ's
 * components/ui/Button). Primary is a brand-accent gradient; MarketplacesIQ's
 * accent is amber/bronze (AdsIQ uses emerald) — the one place the products
 * diverge visually. ghost/subtle/danger/link use shared tokens.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 rounded-md font-sans text-sm font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        primary:
          "text-white shadow-primary bg-gradient-to-r from-[#B45309] to-[#D97706] hover:brightness-95 active:brightness-90",
        ghost:
          "bg-transparent border border-border-light text-text-secondary hover:bg-bg-subtle hover:text-text-primary",
        subtle:
          "bg-bg-subtle text-text-secondary hover:bg-bg-hover hover:text-text-primary",
        danger:
          "bg-status-declining text-white hover:opacity-90",
        link:
          "text-accent hover:underline underline-offset-2",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-9 px-5",
        lg: "h-10 px-6 text-base",
        icon: "h-9 w-9 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";
